import {KeyboardEvent, useEffect, useMemo, useState} from 'react';
import {Box, Dialog, DialogContent, DialogTitle, IconButton, Stack, Typography, useMediaQuery, useTheme} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import {IInlineSubOrder} from '../../../models/Order/IInlineOrder';
import {ISubOrder} from '../../../models/Order/ISubOrder';
import {fetchImage} from '../../../services/ImageService';
import {fetchImageV2} from '../../../services/ImageService';
import {InlineSubOrderGuidDto, SubOrderGuidDto} from '@mybake/ui/models/Order/IOrderGuid';
import {useAppTranslation} from '@mybake/ui/hooks/useAppTranslation';
import {ContentSkeleton} from '../../common/loading/ContentSkeleton';
import {getApiErrorMessage} from '../../../common/apiErrors';

interface Props {
    subOrders: Array<IInlineSubOrder | ISubOrder | InlineSubOrderGuidDto | SubOrderGuidDto>;
    contract?: 'legacy' | 'guid';
}

interface GalleryImage {
    id: string;
    fileName: string;
    imageId?: string;
    recipeName: string;
    isResultImage: boolean;
    url?: string;
}

// Cumulative retry moments: ~1s, 3s, 7s, 15s and 30s after the initial request.
const IMAGE_RETRY_DELAYS = [1000, 2000, 4000, 8000, 15000];

export const OrderImageGallery = ({subOrders, contract = 'legacy'}: Props) => {
    const {t} = useAppTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [refresh, setRefresh] = useState(0);
    const [open, setOpen] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [images, setImages] = useState<GalleryImage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [imageError, setImageError] = useState('');

    const imageMetadata = useMemo(() => subOrders.flatMap(subOrder => (subOrder.images ?? []).map(image => ({
        id: `${image.id}-${image.fileName}`,
        fileName: image.fileName,
        imageId: 'imageId' in image ? image.imageId : undefined,
        recipeName: subOrder.recipeName ?? '',
        isResultImage: Boolean('isResult' in image ? image.isResult : image.isResultImage),
    }))), [subOrders]);

    useEffect(() => {
        let active = true;
        let loadedImages: GalleryImage[] = [];
        const retryTimeouts: number[] = [];
        let completedInitialRequests = 0;
        let completedImageLoads = 0;

        const waitForRetry = (delay: number) => new Promise<void>(resolve => {
            const timeout = window.setTimeout(resolve, delay);
            retryTimeouts.push(timeout);
        });

        const loadImageWithRetry = async (image: GalleryImage) => {
            try {
                for (let attempt = 0; attempt <= IMAGE_RETRY_DELAYS.length && active; attempt += 1) {
                    const result = image.imageId
                        ? await fetchImageV2(image.imageId)
                        : await fetchImage(image.fileName);

                    if (attempt === 0) {
                        completedInitialRequests += 1;
                        if (active && completedInitialRequests === imageMetadata.length) setIsLoading(false);
                    }

                    if (result.imageUrl) {
                        if ('expiresAt' in result && typeof result.expiresAt === 'string') {
                            retryTimeouts.push(window.setTimeout(() => { if (active) setRefresh(value => value + 1); }, Math.max(1000, Date.parse(result.expiresAt) - Date.now() - 10000)));
                        }
                        const loadedImage = {...image, url: result.imageUrl};
                        if (!active) {
                            URL.revokeObjectURL(result.imageUrl);
                            return;
                        }

                        loadedImages.push(loadedImage);
                        setImages(current => [...current, loadedImage].sort((first, second) =>
                            imageMetadata.findIndex(metadata => metadata.id === first.id) - imageMetadata.findIndex(metadata => metadata.id === second.id)));
                        return;
                    }

                    if (result.problem && active) setImageError(getApiErrorMessage(result.problem, t));

                    if (attempt < IMAGE_RETRY_DELAYS.length) await waitForRetry(IMAGE_RETRY_DELAYS[attempt]);
                }
            } finally {
                completedImageLoads += 1;
                if (active && completedImageLoads === imageMetadata.length) setIsProcessing(false);
            }
        };

        setImages([]);
        setImageError('');
        setCurrentIndex(0);
        if (!imageMetadata.length) {
            setIsLoading(false);
            setIsProcessing(false);
            return () => undefined;
        }

        setIsLoading(true);
        setIsProcessing(true);
        imageMetadata.forEach(image => void loadImageWithRetry(image));

        return () => {
            active = false;
            retryTimeouts.forEach(timeout => window.clearTimeout(timeout));
            loadedImages.forEach(image => image.url && URL.revokeObjectURL(image.url));
        };
    }, [contract, imageMetadata, t, refresh]);

    if (!imageMetadata.length) return null;
    const currentImage = images[currentIndex];

    const openImage = (index: number) => {
        setCurrentIndex(index);
        setOpen(true);
    };
    const showPrevious = () => setCurrentIndex(index => index === 0 ? images.length - 1 : index - 1);
    const showNext = () => setCurrentIndex(index => index === images.length - 1 ? 0 : index + 1);
    const handleGalleryKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') showPrevious();
        if (event.key === 'ArrowRight') showNext();
    };

    const thumbnail = (image: GalleryImage, index: number, size: number, onClick: () => void, selected = false) => (
        <Box
            component="button"
            type="button"
            key={image.id}
            aria-label={`${image.recipeName || t('orders:images')} ${index + 1}`}
            aria-current={selected ? 'true' : undefined}
            onClick={onClick}
            sx={{
                width: size,
                height: size,
                p: 0,
                border: selected ? 2 : 0,
                borderStyle: 'solid',
                borderColor: selected ? 'primary.main' : 'transparent',
                borderRadius: 1.5,
                overflow: 'hidden',
                flex: '0 0 auto',
                cursor: 'pointer',
                bgcolor: 'grey.100',
                '&:focus-visible': {outline: '3px solid', outlineColor: 'primary.main', outlineOffset: 2},
            }}
        >
            <Box component="img" src={image.url} alt={image.recipeName}
                 sx={{display: 'block', width: '100%', height: '100%', objectFit: 'cover'}}/>
        </Box>
    );

    return (
        <>
            <Box>
                <Typography variant="caption" color="text.secondary">
                    {t('orders:images')} · {imageMetadata.length}
                </Typography>
                {isLoading ? (
                    <Box sx={{minWidth: 220}}><ContentSkeleton variant="inline"/></Box>
                ) : images.length ? (
                    <>
                        <Stack direction="row" spacing={1} sx={{mt: 0.5, overflowX: 'auto', pb: 0.5}}>
                            {images.map((image, index) => thumbnail(image, index, 124, () => openImage(index)))}
                        </Stack>
                        {isProcessing && <Typography variant="body2" color="text.secondary">{t('orders:images_processing')}</Typography>}
                    </>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {isProcessing ? t('orders:images_processing') : imageError || t('orders:images_load_error')}
                    </Typography>
                )}
            </Box>

            <Dialog open={open} onClose={() => setOpen(false)} fullScreen={isMobile} fullWidth maxWidth="md" onKeyDown={handleGalleryKeyDown}>
                <DialogTitle sx={{pr: 6, py: {xs: 1.25, sm: 2}, fontSize: {xs: '1rem', sm: '1.25rem'}}}>
                    {currentImage?.recipeName || t('orders:images')}
                    {currentImage?.isResultImage ? ` · ${t('orders:result_image')}` : ''}
                    {images.length > 1 ? ` · ${currentIndex + 1}/${images.length}` : ''}
                    <IconButton aria-label={t('common:close')} onClick={() => setOpen(false)} sx={{position: 'absolute', right: 8, top: 8}}><CloseIcon/></IconButton>
                </DialogTitle>
                <DialogContent sx={{px: {xs: 0, sm: 3}, pb: {xs: 1, sm: 3}, display: 'flex'}}>
                    {currentImage && (
                        <Stack spacing={1.5} alignItems="center" justifyContent="space-between" sx={{width: '100%', minHeight: 0}}>
                            <Stack direction="row" alignItems="center" justifyContent="center" width="100%" sx={{position: 'relative', flex: 1, minHeight: 0}}>
                                {images.length > 1 && (
                                    <IconButton onClick={showPrevious} aria-label={t('orders:previous_image')} sx={{
                                        position: {xs: 'absolute', sm: 'static'},
                                        left: {xs: 8, sm: 'auto'},
                                        zIndex: 1,
                                        bgcolor: {xs: 'rgba(255,255,255,0.8)', sm: 'transparent'},
                                    }}><ArrowBackIosNewIcon/></IconButton>
                                )}
                                <Box component="img" src={currentImage.url} alt={currentImage.recipeName}
                                     sx={{
                                         width: {xs: '100%', sm: 'auto'},
                                         maxWidth: {xs: '100%', sm: 'calc(100% - 96px)'},
                                         height: {xs: '100%', sm: 'auto'},
                                         maxHeight: {xs: 'calc(100vh - 150px)', sm: '70vh'},
                                         objectFit: 'contain',
                                         mx: 'auto',
                                     }}/>
                                {images.length > 1 && (
                                    <IconButton onClick={showNext} aria-label={t('orders:next_image')} sx={{
                                        position: {xs: 'absolute', sm: 'static'},
                                        right: {xs: 8, sm: 'auto'},
                                        zIndex: 1,
                                        bgcolor: {xs: 'rgba(255,255,255,0.8)', sm: 'transparent'},
                                    }}><ArrowForwardIosIcon/></IconButton>
                                )}
                            </Stack>
                            {images.length > 1 && (
                                <Stack direction="row" spacing={1} sx={{maxWidth: '100%', overflowX: 'auto', px: {xs: 1, sm: 0.5}, py: 0.5, flexShrink: 0}}>
                                    {images.map((image, index) => thumbnail(
                                        image,
                                        index,
                                        56,
                                        () => setCurrentIndex(index),
                                        index === currentIndex,
                                    ))}
                                </Stack>
                            )}
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};
